<template>
    <div class="info-row">
        <span class="info-label">{{ label }}</span>
        <span class="info-value d-flex align-center flex-wrap" style="gap: 8px">
            <code>{{ currentVersion ?? '—' }}</code>
            <Select
                v-if="options.length"
                v-model="selected"
                :options="options"
                optionLabel="label"
                :placeholder="disabled ? 'Job pending…' : 'Send OTA…'"
                size="small"
                class="ota-select"
                :disabled="disabled"
            />
            <Button
                v-if="selected && !disabled"
                icon="pi pi-send"
                label="Send"
                size="small"
                severity="warn"
                :loading="sending"
                @click="confirmSend"
            />
        </span>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useApi } from '@/api'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import Select from 'primevue/select'
import Button from 'primevue/button'

export interface OtaCatalogEntry {
    name: string
    description: string
    type: string
    binUrl: string
}

export type OtaCatalog = Record<string, Record<string, Record<string, OtaCatalogEntry>>>

export interface OtaFirmwareOption {
    version: string
    name: string
    type: string
    model: string
    hwRevision: string
    label: string
}

const props = defineProps<{
    label: string
    currentVersion: string | undefined
    monitorId: string
    /** The firmware type to filter on, e.g. 'EN', 'CO', 'WL' */
    firmwareType: string
    /** The full OTA catalog from the API */
    catalog: OtaCatalog
    /** Shadow device info: { hwNo, hwRev } */
    shadow: { hwNo?: string; hwRev?: number | string } | undefined
    /** Disable OTA controls (e.g. when a job is already pending) */
    disabled?: boolean
}>()

const emit = defineEmits<{
    'ota-sent': []
}>()

const api = useApi()
const confirm = useConfirm()
const toast = useToast()
const selected = ref<OtaFirmwareOption | null>(null)
const sending = ref(false)

const options = computed((): OtaFirmwareOption[] => {
    if (!props.shadow?.hwNo) return []
    const model = String(props.shadow.hwNo)
    const hwRev = String(props.shadow.hwRev ?? '')
    const versions = props.catalog[model]?.[hwRev]
    if (!versions) return []
    return Object.entries(versions)
        .filter(([_, entry]) => entry.type === props.firmwareType)
        .map(([ver, entry]) => ({
            version: ver,
            name: entry.name,
            type: entry.type,
            model,
            hwRevision: hwRev,
            label: `v${ver}${entry.name ? ' — ' + entry.name : ''}`
        }))
        .sort((a, b) => {
            if (a.version === b.version) return 0
            return a.version > b.version ? -1 : 1
        })
})

const confirmSend = () => {
    if (!selected.value) return
    const fw = selected.value
    confirm.require({
        message: `Send ${fw.type} firmware v${fw.version} to ${props.monitorId}?`,
        header: 'Confirm OTA',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-warning',
        accept: () => sendOta(fw)
    })
}

const sendOta = async (fw: OtaFirmwareOption) => {
    try {
        sending.value = true
        const result = await api.putOTAV1({
            monitorId: props.monitorId,
            putOTARequestV1: {
                version: fw.version,
                model: fw.model,
                hwRevision: fw.hwRevision
            }
        })
        toast.add({
            severity: 'success',
            summary: 'OTA Sent',
            detail: `${fw.type} v${fw.version} queued for ${props.monitorId} (job: ${result.jobId})`,
            life: 4000
        })
        selected.value = null
        emit('ota-sent')
    } catch (e) {
        console.error('Error sending OTA', e)
        toast.add({
            severity: 'error',
            summary: 'OTA Failed',
            detail: e instanceof Error ? e.message : 'Unknown error',
            life: 6000
        })
    } finally {
        sending.value = false
    }
}
</script>

<style lang="scss" scoped>
.info-row {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    gap: 16px;

    &:last-child {
        border-bottom: none;
    }
}

.info-label {
    flex: 0 0 160px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #6b7280;
    white-space: nowrap;
}

.info-value {
    flex: 1;
    font-size: 0.9rem;
    min-width: 0;

    code {
        background: rgba(0, 0, 0, 0.04);
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 0.82rem;
    }
}

.ota-select {
    min-width: 180px;
    max-width: 280px;
}
</style>
